import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './modules/auth/AuthContext';
import { LoginPage } from './modules/auth/LoginPage';
import { RegisterPage } from './modules/auth/RegisterPage';
import { ResetPasswordPage } from './modules/auth/ResetPasswordPage';
import { ProtectedRoute } from './modules/auth/ProtectedRoute';
import { AppLayout } from './modules/shared/AppLayout';
import { DashboardPage } from './modules/dashboard/DashboardPage';
import { MySubmissionsPage } from './modules/submissions/MySubmissionsPage';
import { IndividualSubmissionsPage } from './modules/submissions/IndividualSubmissionsPage';
import { AdminLayout, AdminIndexRedirect } from './modules/admin/AdminLayout';
import { RegistrationQueue } from './modules/admin/RegistrationQueue';
import { UserManagement } from './modules/admin/UserManagement';
import { AdminManagement } from './modules/admin/AdminManagement';
import { RolesManagement } from './modules/admin/RolesManagement';
import { SupervisorAssignments } from './modules/admin/SupervisorAssignments';
import { KPISystems } from './modules/admin/KPISystems';
import { UserKPIAssignments } from './modules/admin/UserKPIAssignments';
import { KPILibrary } from './modules/admin/KPILibrary';
import { PISystems } from './modules/admin/PISystems';
import { UserPIAssignments } from './modules/admin/UserPIAssignments';
import { ReportSystems } from './modules/admin/ReportSystems';
import { UserReportAssignments } from './modules/admin/UserReportAssignments';
import { ErrorBoundary } from './modules/shared/ErrorBoundary';
import './styles/global.css';

function App() {
  return (
    <Router>
      <AuthProvider>
        <ErrorBoundary>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: { background: 'var(--color-surface-2)', color: 'var(--color-text)', border: '1px solid var(--color-border)' },
              success: { iconTheme: { primary: 'var(--color-success)', secondary: 'var(--color-surface)' } },
              error: { duration: 6000, iconTheme: { primary: 'var(--color-danger)', secondary: 'var(--color-surface)' } },
            }}
          />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/my-submissions" element={<MySubmissionsPage />} />
              <Route path="/individual-submissions" element={<IndividualSubmissionsPage />} />

              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminIndexRedirect />} />
                <Route path="registrations" element={<RegistrationQueue />} />
                <Route path="users" element={<UserManagement />} />
                <Route
                  path="admin-management"
                  element={
                    <ProtectedRoute requiredRoles={['Admin']}>
                      <AdminManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="roles"
                  element={
                    <ProtectedRoute requiredPermission="can_manage_roles">
                      <RolesManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="supervisors"
                  element={
                    <ProtectedRoute requiredPermission="can_assign_supervisees">
                      <SupervisorAssignments />
                    </ProtectedRoute>
                  }
                />
                <Route path="kpi-systems" element={<KPISystems />} />
                <Route path="kpi-assignments" element={<UserKPIAssignments />} />
                <Route path="kpi-library" element={<KPILibrary />} />
                <Route path="pi-systems" element={<PISystems />} />
                <Route path="pi-assignments" element={<UserPIAssignments />} />
                <Route path="report-systems" element={<ReportSystems />} />
                <Route path="report-assignments" element={<UserReportAssignments />} />
              </Route>

              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  );
}

function Unauthorized() {
  return (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <h2>403 - Unauthorized</h2>
      <p>You don't have permission to access this page.</p>
      <p><a href="/dashboard">Go back to dashboard</a></p>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <h2>404 - Page Not Found</h2>
      <p><a href="/dashboard">Go back to dashboard</a></p>
    </div>
  );
}

export default App;
